import React from 'react';
import { Bookmark, Tag } from '@/types';
import { MoreVertical, Trash2, Edit, Pin, StickyNote, Globe, Link2 } from 'lucide-react';
import ImageWithLoader from '@/components/ImageWithLoader';

interface Props {
  bookmark: Bookmark;
  allTags: Tag[];
  onDelete: (id: string) => void;
  onEdit: (bookmark: Bookmark) => void;
  onTogglePin?: (id: string) => void;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  onToggleSelect?: (id: string, event: React.MouseEvent) => void;
  viewMode?: 'grid' | 'list';
}

// Domain-based gradient palette for thumbnail fallback
const GRADIENTS = [
  'from-sky-400 via-blue-500 to-indigo-600',
  'from-violet-400 via-purple-500 to-fuchsia-600',
  'from-fuchsia-400 via-pink-500 to-rose-600',
  'from-amber-400 via-orange-500 to-red-500',
  'from-emerald-400 via-teal-500 to-cyan-600',
  'from-cyan-400 via-sky-500 to-blue-600',
  'from-rose-400 via-red-500 to-orange-600',
  'from-indigo-400 via-violet-500 to-purple-600',
];

const hashString = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const getHostname = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const BookmarkCard = ({ bookmark, allTags, onDelete, onEdit, onTogglePin, isSelected, isSelectionMode, onToggleSelect, viewMode = 'grid' }: Props) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const hostname = getHostname(bookmark.url);
  const gradient = GRADIENTS[hashString(hostname || bookmark.title || 'webtags') % GRADIENTS.length];
  const initial = (bookmark.title || hostname || '?').trim().charAt(0).toUpperCase();
  const displayUrl = bookmark.url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Attractive gradient placeholder shown when no thumbnail / thumbnail fails
  const ThumbFallback = ({ compact = false }: { compact?: boolean }) => (
    <div className={`w-full h-full bg-gradient-to-br ${gradient} flex flex-col items-center justify-center gap-2 select-none`}>
      <div className={`${compact ? 'w-9 h-9 rounded-lg p-1' : 'w-14 h-14 rounded-2xl p-2.5'} bg-white/95 shadow-md flex items-center justify-center overflow-hidden`}>
        {bookmark.favicon ? (
          <ImageWithLoader src={bookmark.favicon} className="w-full h-full" />
        ) : (
          <span className={`font-bold text-slate-700 ${compact ? 'text-sm' : 'text-2xl'}`}>{initial}</span>
        )}
      </div>
      {!compact && hostname && (
        <span className="text-white/90 text-[11px] font-medium tracking-wide px-3 max-w-full truncate">{hostname}</span>
      )}
    </div>
  );

  // List View
  if (viewMode === 'list') {
      return (
          <div
            className={`bg-white dark:bg-slate-800 rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 border relative group flex items-center p-3 gap-4 h-20 ${isSelected ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600'}`}
            onClick={(e) => {
                if (isSelectionMode && onToggleSelect) {
                    e.preventDefault();
                    onToggleSelect(bookmark.id, e);
                } else {
                    if (!(e.target as HTMLElement).closest('button, .checkbox-area')) {
                        window.open(bookmark.url, '_blank');
                    }
                }
            }}
          >
              {/* Checkbox */}
              {(isSelectionMode || isSelected) && (
                  <div 
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-colors cursor-pointer shrink-0 ${isSelected ? 'bg-blue-500 border-blue-500' : 'bg-white/80 border-slate-300 hover:border-blue-400'}`}
                      onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(bookmark.id, e); }}
                  >
                      {isSelected && <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                  </div>
              )}

              {/* Thumbnail */}
              <div className="w-24 h-full rounded-lg overflow-hidden shrink-0 relative bg-slate-100 dark:bg-slate-700">
                  {bookmark.thumbnail ? (
                      <ImageWithLoader src={bookmark.thumbnail} alt={bookmark.title} className="w-full h-full object-cover" fallback={<ThumbFallback compact />} />
                  ) : (
                      <ThumbFallback compact />
                  )}
                  {bookmark.isPinned && (
                      <div className="absolute top-1 right-1 bg-blue-500 text-white p-0.5 rounded shadow-sm z-10">
                          <Pin size={10} fill="currentColor" />
                      </div>
                  )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                  <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0 overflow-hidden">
                          {bookmark.favicon ? <ImageWithLoader src={bookmark.favicon} className="w-3 h-3" /> : <span className="text-[10px]">🌐</span>}
                      </div>
                      <h3 className="font-medium text-slate-800 dark:text-slate-200 truncate text-sm" title={bookmark.title}>{bookmark.title}</h3>
                  </div>
                  {bookmark.notes && (
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400" title={bookmark.notes}>
                          <StickyNote size={11} className="shrink-0 text-amber-500" />
                          <span className="truncate">{bookmark.notes}</span>
                      </div>
                  )}
                  <div className="flex flex-wrap gap-1">
                      {bookmark.tags.map(tagId => {
                          const tag = allTags.find(t => t.id === tagId);
                          if (!tag) return null;
                          return (
                              <span key={tag.id} className="text-[10px] px-2 py-0.5 rounded-full border border-transparent" style={{ backgroundColor: `${tag.color}15`, color: tag.color, borderColor: `${tag.color}30` }}>
                                  {tag.name}
                              </span>
                          );
                      })}
                  </div>
              </div>

              {/* Actions */}
              <div className="relative opacity-0 group-hover:opacity-100 transition-opacity" ref={menuRef}>
                  <button 
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(!menuOpen); }} 
                      className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full text-slate-500"
                  >
                      <MoreVertical size={16} />
                  </button>
                  {menuOpen && (
                      <div className="absolute top-8 right-0 bg-white dark:bg-slate-800 shadow-xl rounded-lg py-1 border border-slate-100 dark:border-slate-700 w-32 overflow-hidden animate-in fade-in zoom-in-95 duration-100 z-50">
                          {onTogglePin && (
                              <button 
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(bookmark.id); setMenuOpen(false); }} 
                                  className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"
                              >
                                  <Pin size={14} className={bookmark.isPinned ? 'fill-current text-blue-500' : ''} /> {bookmark.isPinned ? '取消置顶' : '置顶'}
                              </button>
                          )}
                          <button 
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(bookmark); setMenuOpen(false); }} 
                              className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"
                          >
                              <Edit size={14} /> 编辑
                          </button>
                          <button 
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(bookmark.id); setMenuOpen(false); }} 
                              className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 flex items-center gap-2"
                          >
                              <Trash2 size={14} /> 删除
                          </button>
                      </div>
                  )}
              </div>
          </div>
      );
  }

  // Grid View
  return (
    <div
        className={`bg-white dark:bg-slate-800 rounded-xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-200 border relative group h-full flex flex-col overflow-hidden ${isSelected ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600'}`}
        onClick={(e) => {
            if (isSelectionMode && onToggleSelect) {
                e.preventDefault();
                onToggleSelect(bookmark.id, e);
            } else {
                if (!(e.target as HTMLElement).closest('button, .checkbox-area')) {
                    window.open(bookmark.url, '_blank');
                }
            }
        }}
    >
      <div className="relative aspect-video overflow-hidden bg-slate-100 dark:bg-slate-700 group/image shrink-0">
        {/* Pin Indicator */}
        {bookmark.isPinned && (
            <div className="absolute top-2 left-2 z-10 bg-white/95 dark:bg-slate-800/95 text-blue-500 p-1.5 rounded-lg shadow-sm backdrop-blur-sm" title="已置顶">
                <Pin size={12} fill="currentColor" />
            </div>
        )}

        {(isSelectionMode || isSelected) && (
            <div className="absolute top-2 left-2 z-20 checkbox-area">
                <div
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors cursor-pointer ${isSelected ? 'bg-blue-500 border-blue-500' : 'bg-white/80 border-slate-300 hover:border-blue-400'}`}
                    onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(bookmark.id, e); }}
                >
                    {isSelected && <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </div>
            </div>
        )}

        {/* Checkbox for non-selection mode (hover only) */}
        {!isSelectionMode && onToggleSelect && (
             <div className={`absolute top-2 left-2 z-20 transition-opacity checkbox-area ${bookmark.isPinned ? 'opacity-0 hover:opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                <button
                    onClick={(e) => { e.stopPropagation(); onToggleSelect(bookmark.id, e); }}
                    className="w-5 h-5 rounded border border-slate-300 bg-white/80 hover:border-blue-400 flex items-center justify-center"
                />
            </div>
        )}

        {bookmark.thumbnail ? (
          <div className="w-full h-full transition-transform duration-300 group-hover:scale-[1.04]">
            <ImageWithLoader src={bookmark.thumbnail} alt={bookmark.title} className="w-full h-full" fallback={<ThumbFallback />} />
          </div>
        ) : (
          <div className="w-full h-full transition-transform duration-300 group-hover:scale-[1.04]">
            <ThumbFallback />
          </div>
        )}
      </div>

      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-20" ref={menuRef}>
           <button 
             onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(!menuOpen); }} 
             className="p-1.5 bg-white/90 dark:bg-slate-800/90 rounded-full hover:bg-white dark:hover:bg-slate-700 shadow-sm backdrop-blur-sm text-slate-600 dark:text-slate-300"
           >
             <MoreVertical size={16} />
           </button>
           {menuOpen && (
            <div className="absolute top-8 right-0 bg-white dark:bg-slate-800 shadow-xl rounded-lg py-1 border border-slate-100 dark:border-slate-700 w-32 overflow-hidden animate-in fade-in zoom-in-95 duration-100 z-30">
                {onTogglePin && (
                    <button 
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(bookmark.id); setMenuOpen(false); }} 
                        className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"
                    >
                        <Pin size={14} className={bookmark.isPinned ? 'fill-current text-blue-500' : ''} /> {bookmark.isPinned ? '取消置顶' : '置顶'}
                    </button>
                )}
                <button 
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(bookmark); setMenuOpen(false); }} 
                    className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2 text-slate-700 dark:text-slate-200"
                >
                    <Edit size={14} /> 编辑
                </button>
                <button 
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(bookmark.id); setMenuOpen(false); }} 
                    className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 flex items-center gap-2"
                >
                    <Trash2 size={14} /> 删除
                </button>
            </div>
        )}
      </div>
      
      <div className="p-4 flex-1 flex flex-col gap-2" title={bookmark.url}>
        <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0 overflow-hidden">
                {bookmark.favicon ? <ImageWithLoader src={bookmark.favicon} className="w-3.5 h-3.5" /> : <span className="text-xs">🌐</span>}
            </div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-200 truncate flex-1 text-sm" title={bookmark.title}>{bookmark.title}</h3>
        </div>
        {bookmark.notes ? (
            <div className="flex items-start gap-1.5 rounded-lg bg-slate-50 dark:bg-slate-900/60 border border-slate-100 dark:border-slate-700 px-2.5 py-1.5 text-[11px] text-slate-500 dark:text-slate-400" title={bookmark.notes}>
                <StickyNote size={12} className="shrink-0 mt-0.5 text-amber-500" />
                <span className="line-clamp-2 break-all font-mono leading-relaxed">{bookmark.notes}</span>
            </div>
        ) : (
            <div className="flex items-start gap-1.5 rounded-lg bg-slate-50/60 dark:bg-slate-900/40 border border-slate-100/80 dark:border-slate-700/60 px-2.5 py-1.5 text-[11px] text-slate-400 dark:text-slate-500" title={bookmark.url}>
                <Link2 size={12} className="shrink-0 mt-0.5" />
                <span className="truncate leading-relaxed">{displayUrl}</span>
            </div>
        )}
        <div className="mt-auto pt-1 flex items-end justify-between gap-2">
            <div className="flex flex-wrap gap-1 min-w-0">
                {bookmark.tags.map(tagId => {
                    const tag = allTags.find(t => t.id === tagId);
                    if (!tag) return null;
                    return (
                        <span key={tag.id} className="text-[10px] px-2 py-0.5 rounded-full border border-transparent font-medium" style={{ backgroundColor: `${tag.color}15`, color: tag.color, borderColor: `${tag.color}30` }}>
                            {tag.name}
                        </span>
                    );
                })}
            </div>
            {hostname && (
                <span className="shrink-0 flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500" title={bookmark.url}>
                    <Globe size={10} />
                    <span className="max-w-[100px] truncate">{hostname}</span>
                </span>
            )}
        </div>
      </div>
    </div>
  );
};

export default BookmarkCard;
